import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useOutletContext } from "react-router-dom";
import { meApi } from "../../api/me";
import type { UserOut } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { formatDateTime, utcTooltip } from "../../shared/format";
import { useInvalidateMe } from "../../shared/hooks/useMe";
import { Alert, Button, Card, Input, Modal } from "../../ui";
import { MfaCard } from "./MfaCard";
import { PasskeysCard } from "./PasskeysCard";

export function SecurityPage() {
  const me = useOutletContext<UserOut>();
  const queryClient = useQueryClient();
  const invalidateMe = useInvalidateMe();
  const sessions = useQuery({
    queryKey: ["me", "sessions"],
    queryFn: meApi.sessions,
  });
  const consents = useQuery({
    queryKey: ["me", "consents"],
    queryFn: meApi.consents,
  });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["me"] });

  const changePassword = useMutation({
    mutationFn: () =>
      meApi.changePassword({
        current_password: me.has_password ? pw.current_password : undefined,
        new_password: pw.new_password,
      }),
    onSuccess: async () => {
      setMessage({
        kind: "success",
        text: "Password changed. Other sessions were signed out.",
      });
      setPw({ current_password: "", new_password: "" });
      await invalidateMe();
      await refresh();
    },
    onError: (err) => setMessage({ kind: "error", text: errorMessage(err) }),
  });
  const revokeSession = useMutation({
    mutationFn: (id: string) => meApi.revokeSession(id),
    onSuccess: refresh,
  });
  const revokeOthers = useMutation({
    mutationFn: () => meApi.revokeOtherSessions(),
    onSuccess: () => {
      setConfirmAll(false);
      return refresh();
    },
  });
  const revokeConsent = useMutation({
    mutationFn: (id: string) => meApi.revokeConsent(id),
    onSuccess: refresh,
  });

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <h1 className="mb-4 text-xl font-semibold">Password</h1>
        {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
        <form
          className="mt-3 flex max-w-md flex-col gap-4"
          aria-label="Change password"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            changePassword.mutate();
          }}
        >
          {me.has_password ? (
            <Input
              label="Current password"
              type="password"
              autoComplete="current-password"
              required
              value={pw.current_password}
              onChange={(e) =>
                setPw({ ...pw, current_password: e.target.value })
              }
            />
          ) : (
            <p className="text-sm text-slate-600">
              You sign in with Google. Set a password to also sign in with
              email.
            </p>
          )}
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
          />
          <Button type="submit" loading={changePassword.isPending}>
            {me.has_password ? "Change password" : "Set password"}
          </Button>
        </form>
      </Card>
      <MfaCard me={me} onChanged={invalidateMe} />
      <PasskeysCard onChanged={invalidateMe} />
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Sessions</h2>
          <Button
            variant="secondary"
            onClick={() => setConfirmAll(true)}
            disabled={(sessions.data?.length ?? 0) < 2}
          >
            Sign out other sessions
          </Button>
        </div>
        <ul className="divide-y divide-slate-100 text-sm">
          {(sessions.data ?? []).map((s) => (
            <li key={s.id} className="flex items-center justify-between py-2">
              <div>
                <div>
                  {s.user_agent ?? "Unknown device"}{" "}
                  {s.current ? (
                    <span className="ml-2 rounded bg-green-100 px-1 text-xs text-green-800">
                      this session
                    </span>
                  ) : null}
                </div>
                <div
                  className="text-xs text-slate-500"
                  title={utcTooltip(s.last_seen_at)}
                >
                  {s.last_ip ?? "—"} · last seen{" "}
                  {formatDateTime(s.last_seen_at)}
                </div>
              </div>
              {!s.current ? (
                <Button
                  variant="secondary"
                  onClick={() => revokeSession.mutate(s.id)}
                >
                  Sign out
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        <Modal
          open={confirmAll}
          title="Sign out other sessions?"
          onClose={() => setConfirmAll(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmAll(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => revokeOthers.mutate()}
                loading={revokeOthers.isPending}
              >
                Sign out
              </Button>
            </>
          }
        >
          Every other browser and device will need to sign in again.
        </Modal>
      </Card>
      <Card>
        <h2 className="mb-4 text-xl font-semibold">Connected apps</h2>
        {(consents.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">
            No third-party apps have access to your account.
          </p>
        ) : null}
        <ul className="divide-y divide-slate-100 text-sm">
          {(consents.data ?? []).map((c) => (
            <li
              key={c.client_id}
              className="flex items-center justify-between py-2"
            >
              <div>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-slate-500">
                  {c.scope} · granted {formatDateTime(c.granted_at)}
                </div>
              </div>
              <Button
                variant="secondary"
                onClick={() => revokeConsent.mutate(c.client_id)}
              >
                Remove access
              </Button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
